import { useCallback, useLayoutEffect, useRef, useState } from "react";

/* Adapted from Bencho's MIT-licensed Icon bar. See IconBar.LICENSE.txt. */
/* Elastic, as numbers a person can hold. 50 keeps the source's tuned motion. */
const rate = (speed) => 1.6 - (speed / 100) * 1.2;
/* The bezier's overshoot sets how much the indicator gives when it lands. */
const overshoot = (bounce, tuned) =>
  Number((1 + (bounce / 100) * (tuned - 1) * 2).toFixed(3));
const curve = (bounce, tuned, x1, x2) =>
  `cubic-bezier(${x1}, ${overshoot(bounce, tuned)}, ${x2}, 1)`;

const variables = {
  "--ind-stretch": `${190 * rate(50)}ms`,
  "--ind-settle": `${420 * rate(50)}ms`,
  /* Move and width overshoot by different amounts: the box arrives before its edges finish. */
  "--ind-move": curve(50, 1.28, 0.28, 0.36),
  "--ind-size": curve(50, 1.34, 0.24, 0.38),
};

const lastSelection = new Map();

export default function useJellyIndicator(value, axis = "x", handoffKey = null) {
  const trackRef = useRef(null);
  const itemRefs = useRef({});
  const selectedRef = useRef(value);
  const indicatorRef = useRef(null);
  const timer = useRef(null);
  const [indicator, setIndicator] = useState(null);
  const [phase, setPhase] = useState("idle");

  const measure = useCallback((key) => {
    const element = itemRefs.current[key];
    if (!element) return null;
    return axis === "y"
      ? { p: element.offsetTop, s: element.offsetHeight }
      : { p: element.offsetLeft, s: element.offsetWidth };
  }, [axis]);

  useLayoutEffect(() => {
    let frame;
    let settleTimer;
    let animating = false;
    const align = () => {
      window.clearTimeout(timer.current);
      const next = measure(selectedRef.current);
      indicatorRef.current = next;
      setIndicator(next);
      setPhase("idle");
    };

    const previousKey = handoffKey ? lastSelection.get(handoffKey) : null;
    const from = measure(previousKey);
    const to = measure(selectedRef.current);
    if (from && to && previousKey !== selectedRef.current && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      indicatorRef.current = from;
      setIndicator(from);
      setPhase("idle");
      animating = true;
      frame = window.requestAnimationFrame(() => {
        const start = Math.min(from.p, to.p);
        const end = Math.max(from.p + from.s, to.p + to.s);
        setIndicator({ p: start, s: end - start });
        setPhase("stretch");
        timer.current = window.setTimeout(() => {
          indicatorRef.current = to;
          setIndicator(to);
          setPhase("settle");
          settleTimer = window.setTimeout(() => { animating = false; }, 420 * rate(50));
        }, 150 * rate(50));
      });
    } else {
      align();
    }

    const observer = new ResizeObserver(() => { if (!animating) align(); });
    if (trackRef.current) observer.observe(trackRef.current);
    Object.values(itemRefs.current).forEach((element) => {
      if (element) observer.observe(element);
    });
    return () => {
      if (handoffKey) lastSelection.set(handoffKey, selectedRef.current);
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer.current);
      window.clearTimeout(settleTimer);
    };
  }, [measure, handoffKey]);

  useLayoutEffect(() => {
    if (value === selectedRef.current) return;
    window.clearTimeout(timer.current);
    selectedRef.current = value;
    const to = measure(value);
    const from = indicatorRef.current;

    if (!to || !from || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      indicatorRef.current = to;
      setIndicator(to);
      setPhase("idle");
      return;
    }

    /* The full union of both slots is the farthest the pill can stretch. */
    const start = Math.min(from.p, to.p);
    const end = Math.max(from.p + from.s, to.p + to.s);
    setIndicator({ p: start, s: end - start });
    setPhase("stretch");

    /* Handoff follows the stretch clock so the landing reads as one motion. */
    timer.current = window.setTimeout(() => {
      indicatorRef.current = to;
      setIndicator(to);
      setPhase("settle");
    }, 150 * rate(50));
  }, [value, measure]);

  return {
    trackRef,
    itemRefs,
    phase,
    variables,
    indicatorStyle: indicator
      ? axis === "y"
        ? { transform: `translate3d(0, ${indicator.p}px, 0)`, height: indicator.s }
        : { transform: `translate3d(${indicator.p}px, 0, 0)`, width: indicator.s }
      : { opacity: 0 },
  };
}
